import { Card, CardContent } from '@design-system';
import { durationInWords, type CareScenarioSummary } from '@shared/consumerPlan';
import { CARE_SETTING_COPY } from '../lib/copy';
import { hoursPerWeek } from '../lib/format';
import { Figure, MetricGrid, metricFigureClassName } from './figures';

function settingLabel(scenario: CareScenarioSummary) {
  return scenario.careSetting ? CARE_SETTING_COPY[scenario.careSetting].title : 'Your chosen settings';
}

function Value({ children }: { children: number }) {
  return <strong className="font-semibold tabular-nums text-gray-950">{children}</strong>;
}

function unit(count: number, singular: string, plural = `${singular}s`) {
  return (
    <strong className="font-semibold text-gray-950">
      <Value>{count}</Value>
      {'\u00a0'}
      {count === 1 ? singular : plural}
    </strong>
  );
}

function durationPhrase(totalMonths: number) {
  const rounded = Math.round(totalMonths);
  const years = Math.floor(rounded / 12);
  const months = rounded % 12;

  return (
    <>
      {years > 0 && unit(years, 'year')}
      {years > 0 && months > 0 && ' and '}
      {(months > 0 || years === 0) && unit(months, 'month')}
    </>
  );
}

function SentenceSummary({ scenario }: { scenario: CareScenarioSummary }) {
  return (
    <aside
      role="note"
      data-testid="consumer-scenario-summary"
      className="bg-muted/70 w-full rounded-lg px-3 py-2.5 text-sm leading-6 text-gray-900"
    >
      <p className="text-pretty">
        <strong className="font-semibold text-gray-950">Care scenario:</strong> Start at{' '}
        <strong className="font-semibold text-gray-950">
          age{'\u00a0'}
          <Value>{scenario.careStartAge}</Value>
        </strong>{' '}
        and last for {durationPhrase(scenario.totalMonths)}.{' '}
        {settingLabel(scenario)}.
      </p>
    </aside>
  );
}

/** The care scenario as a metric bar, or as a one-line recap. */
export function ScenarioSummary({
  scenario,
  variant = 'card',
}: {
  scenario: CareScenarioSummary;
  variant?: 'card' | 'sentence';
}) {
  if (variant === 'sentence') {
    return <SentenceSummary scenario={scenario} />;
  }

  const setting = settingLabel(scenario);
  const start = `Age ${scenario.careStartAge} · ${durationInWords(scenario.totalMonths)}`;
  const hours = hoursPerWeek(scenario.careHoursPerWeek);

  return (
    <Card className="py-4">
      <CardContent className="px-4">
        <MetricGrid>
          <Figure label="Projected care start/duration" value={start} className={metricFigureClassName} />
          <Figure label="Where you want care" value={setting} className={metricFigureClassName} />
          <Figure label="Projected weekly care" value={hours} className={metricFigureClassName} />
        </MetricGrid>
      </CardContent>
    </Card>
  );
}
